import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router"
import useTitle from "@/hooks/useTitle"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import PulseLoader from "react-spinners/PulseLoader"

import {
  useGetAccountContactMapsQuery,
  useUpdateAccountContactMapMutation,
} from "./accountContactMapsApiSlice"

const EtUpdateAccountContactMap = ({ id, setActiveAdminView }) => {

  // First Created - 27 Apr 2026
  // Author - Atul Pandey
  // Purpose - To update an Account Contact Map
  // Updated - 28 Apr 2026 -  refined for reportsToContactId

  // console.log('id for update account contact map : ', id)
 

  useTitle("Update Account Contact Map")

  // 0 - Incoming data extraction
  const mapId = id?.mapId || id
  const accountIdFromList = id?.accountId || ""

  // console.log("incoming id:", id)
  // console.log("mapId:", mapId)
  // console.log("accountIdFromList:", accountIdFromList)

  
  // 1 - State Variables
  // const [selectedAccountId, setSelectedAccountId] = useState("")
  const [selectedAccountId, setSelectedAccountId] = useState(accountIdFromList)

  // 2 - Data
  const { data: initialMapsData } = useGetAccountContactMapsQuery(
    { accountId: selectedAccountId },
    { skip: !selectedAccountId }
  )
  // const { data: initialMapsData } = useGetAccountContactMapsQuery()
  

  // console.log('initialMapsData :', initialMapsData)

  const allMaps =
    initialMapsData?.ids?.map(mapId => initialMapsData?.entities[mapId]) || []

  // console.log('allMaps :', allMaps)

  // const selectedMap = allMaps?.find(item => item?._id === id || item?.id === id)
  const selectedMap = allMaps?.find(item => item?._id === mapId || item?.id === mapId)

  useEffect(() => {
    if (!selectedAccountId && selectedMap?.accountId) {
      setSelectedAccountId(selectedMap?.accountId?._id || selectedMap?.accountId?.id || selectedMap?.accountId)
    }
  }, [selectedMap, selectedAccountId])

  if (!selectedMap) {
    return (
      <div className="bg-gray-200 p-2 rounded">
        <p className="text-xs text-gray-700">
          Please open this update from the Account Contact Map list after selecting the account.
        </p>
      </div>
    )
  }

  return (
    <EtUpdateAccountContactMapForm
      accountContactMap={selectedMap}
      // id={id}
      id={mapId} // 28 Apr 2026
      setActiveAdminView={setActiveAdminView}
      accountContactMaps={allMaps}
    />
  )
}

const EtUpdateAccountContactMapForm = ({
  accountContactMap,
  id,
  setActiveAdminView,
  accountContactMaps = [],
}) => {
  const navigate = useNavigate()

  const [updateAccountContactMap, { isLoading, isSuccess, isError, error }] =
    useUpdateAccountContactMapMutation()

  const getIdValue = value => value?._id || value?.id || value || ""

  const accountId = getIdValue(accountContactMap.accountId)
  const contactId = getIdValue(accountContactMap.contactId)

  const [formData, setFormData] = useState({
    accountId,
    contactId,
    reportsToContactId: getIdValue(accountContactMap.reportsToContactId), // 28 Apr 2026
    reportsToAccountContactMapId: getIdValue(accountContactMap.reportsToAccountContactMapId),
    internalOwnerUserId: getIdValue(accountContactMap.internalOwnerUserId),
    contactMapRole: accountContactMap.contactMapRole || "",
    buyingRole: accountContactMap.buyingRole || "",
    influenceLevel: accountContactMap.influenceLevel || "",
    relationshipStrength: accountContactMap.relationshipStrength || "Unknown",
    relationshipSentiment: accountContactMap.relationshipSentiment || "Unknown",
    conversationCadence: accountContactMap.conversationCadence || "",
    lastMeaningfulConversationDate: accountContactMap.lastMeaningfulConversationDate
      ? accountContactMap.lastMeaningfulConversationDate.slice(0, 10)
      : "",
    nextPlannedConversationDate: accountContactMap.nextPlannedConversationDate
      ? accountContactMap.nextPlannedConversationDate.slice(0, 10)
      : "",
    strategicImportance: accountContactMap.strategicImportance || "",
    accessLevel: accountContactMap.accessLevel || "NoAccess",
    contactMapNotes: accountContactMap.contactMapNotes?.length
      ? accountContactMap.contactMapNotes
      : [{ noteText: "", noteDate: "", addedByUserId: "" }],
    active: accountContactMap.active ?? true,
  })

  useEffect(() => {
    if (isSuccess) {
      if (setActiveAdminView) {
        setActiveAdminView("accountContactMaps")
      } else {
        navigate("/accountContactMaps")
      }
    }
  }, [isSuccess])

  const handleChange = e => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const handleAddNote = () => {
    setFormData(prev => ({
      ...prev,
      contactMapNotes: [
        ...prev.contactMapNotes,
        { noteText: "", noteDate: "", addedByUserId: "" },
      ],
    }))
  }

  const handleRemoveNote = index => {
    setFormData(prev => ({
      ...prev,
      contactMapNotes: prev.contactMapNotes.filter((_, i) => i !== index),
    }))
  }

  const handleNoteChange = (index, fieldName, value) => {
    const updatedNotes = formData.contactMapNotes.map((note, i) =>
      i === index ? { ...note, [fieldName]: value } : note
    )

    setFormData(prev => ({
      ...prev,
      contactMapNotes: updatedNotes,
    }))
  }

  const handleSubmit = async e => {
    e.preventDefault()

    const cleanedNotes = formData.contactMapNotes.filter(note =>
      note.noteText?.trim()
    )

    const payload = {
      id,
      // id: mapId, // 28 Apr 2026
      accountId: formData.accountId,
      contactId: formData.contactId,
      reportsToContactId: formData.reportsToContactId || null, // 28 Apr 2026
      reportsToAccountContactMapId: formData.reportsToAccountContactMapId || null,
      internalOwnerUserId: formData.internalOwnerUserId || null,
      contactMapRole: formData.contactMapRole,
      buyingRole: formData.buyingRole,
      influenceLevel: formData.influenceLevel,
      relationshipStrength: formData.relationshipStrength,
      relationshipSentiment: formData.relationshipSentiment,
      conversationCadence: formData.conversationCadence,
      lastMeaningfulConversationDate: formData.lastMeaningfulConversationDate || null,
      nextPlannedConversationDate: formData.nextPlannedConversationDate || null,
      strategicImportance: formData.strategicImportance,
      accessLevel: formData.accessLevel,
      contactMapNotes: cleanedNotes,
      active: formData.active,
    }

    try {
      await updateAccountContactMap(payload).unwrap()
    } catch (error) {
      console.error(error)
    }
  }

  const handleCancel = () => {
    if (setActiveAdminView) {
      setActiveAdminView("accountContactMaps")
    } else {
      navigate("/accountContactMaps")
    }
  }

  const contactName =
    accountContactMap?.contactId?.contactName ||
    accountContactMap?.contactId?.name ||
    "Selected Contact"

  const eligibleReportsTo = accountContactMaps?.filter(
    item => (item?._id || item?.id) !== id
  )

  // 28 Apr 2026
  const eligibleReportsToContacts = accountContactMaps
  ?.map(item => item?.contactId)
  ?.filter(contact => {
    const reportsToContactId = contact?._id || contact?.id
    return reportsToContactId?.toString() !== contactId?.toString()
  })

  return (
    <div className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black">
      {isLoading && <PulseLoader color={"#ea580c"} />}

      {isError && (
        <p className="text-orange-700 font-semi-bold py-2">
          {error?.data?.message}
        </p>
      )}

      <div className="bg-white p-1 m-1 rounded">
        <p className="p-1 m-1 font-bold font-poppins text-xl">
          Update Account Contact Map
        </p>

        <div className="bg-gray-200 rounded m-1 p-1">
          <form onSubmit={handleSubmit} className="m-1 p-1 text-xs font-poppins">
            <div className="bg-white rounded m-1 p-2">
              <p className="text-xs font-bold text-gray-700">{contactName}</p>
              <p className="text-[10px] text-gray-500">
                {accountContactMap?.contactId?.contactEmail || "No email"}
              </p>
            </div>

            {/* 28 Apr 2026 */}
            <select
                name="reportsToContactId"
                value={formData.reportsToContactId}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
                >
                <option value="">Reports To Contact</option>
                {eligibleReportsToContacts?.map(contact => (
                    <option key={contact?._id || contact?.id} value={contact?._id || contact?.id}>
                    {contact?.contactName || "Contact"}
                    {contact?.contactEmail ? ` (${contact.contactEmail})` : ""}
                    </option>
                ))}
            </select>

            <select
              name="reportsToAccountContactMapId"
              value={formData.reportsToAccountContactMapId}
              onChange={handleChange}
              className="w-full rounded p-2 m-1"
            >
              <option value="">Reports To Existing Account Map Entry</option>
              {eligibleReportsTo?.map(mapEntry => (
                <option key={mapEntry?._id || mapEntry?.id} value={mapEntry?._id || mapEntry?.id}>
                  {mapEntry?.contactId?.contactName || "Mapped Contact"}
                </option>
              ))}
            </select>

            <input
              type="text"
              name="contactMapRole"
              placeholder="Contact map role"
              value={formData.contactMapRole}
              onChange={handleChange}
              className="w-full rounded p-2 m-1"
            />

            <input
              type="text"
              name="buyingRole"
              placeholder="Buying role"
              value={formData.buyingRole}
              onChange={handleChange}
              className="w-full rounded p-2 m-1"
            />

            <div className="grid grid-cols-2 gap-1">
              <select name="influenceLevel" value={formData.influenceLevel} onChange={handleChange} className="w-full rounded p-2 m-1">
                <option value="">Influence Level</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Very High">Very High</option>
              </select>

              <select name="relationshipStrength" value={formData.relationshipStrength} onChange={handleChange} className="w-full rounded p-2 m-1">
                <option value="Unknown">Unknown</option>
                <option value="Weak">Weak</option>
                <option value="Developing">Developing</option>
                <option value="Strong">Strong</option>
                <option value="Trusted">Trusted</option>
              </select>

              <select name="relationshipSentiment" value={formData.relationshipSentiment} onChange={handleChange} className="w-full rounded p-2 m-1">
                <option value="Unknown">Unknown</option>
                <option value="Negative">Negative</option>
                <option value="Neutral">Neutral</option>
                <option value="Positive">Positive</option>
                <option value="Very Positive">Very Positive</option>
              </select>

              <select name="conversationCadence" value={formData.conversationCadence} onChange={handleChange} className="w-full rounded p-2 m-1">
                <option value="">Conversation Cadence</option>
                <option value="Weekly">Weekly</option>
                <option value="BiWeekly">BiWeekly</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="AdHoc">AdHoc</option>
              </select>

              <select name="strategicImportance" value={formData.strategicImportance} onChange={handleChange} className="w-full rounded p-2 m-1">
                <option value="">Strategic Importance</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>

              <select name="accessLevel" value={formData.accessLevel} onChange={handleChange} className="w-full rounded p-2 m-1">
                <option value="NoAccess">No Access</option>
                <option value="LimitedAccess">Limited Access</option>
                <option value="DirectAccess">Direct Access</option>
                <option value="TrustedAccess">Trusted Access</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-1">
              <input type="date" name="lastMeaningfulConversationDate" value={formData.lastMeaningfulConversationDate} onChange={handleChange} className="w-full rounded p-2 m-1" />

              <input type="date" name="nextPlannedConversationDate" value={formData.nextPlannedConversationDate} onChange={handleChange} className="w-full rounded p-2 m-1" />
            </div>

            <div className="flex justify-start items-center bg-white rounded m-1 p-2">
              <input type="checkbox" name="active" checked={formData.active} onChange={handleChange} className="m-1" />
              <p className="text-xs font-bold text-gray-700 m-1">Active Contact Map Entry</p>
            </div>

            <div className="m-1 p-1 border border-white rounded">
              <p className="font-bold mx-2 px-2 mt-2">Contact Map Notes</p>

              {formData.contactMapNotes.map((note, index) => (
                <div key={index} className="grid grid-cols-12 gap-1 items-center mx-1">
                  <input
                    type="text"
                    value={note.noteText}
                    onChange={e => handleNoteChange(index, "noteText", e.target.value)}
                    placeholder="Add note"
                    className="col-span-8 rounded p-2 m-1 text-xs"
                  />

                  <input
                    type="date"
                    value={note.noteDate ? note.noteDate.slice(0, 10) : ""}
                    onChange={e => handleNoteChange(index, "noteDate", e.target.value)}
                    className="col-span-3 rounded p-2 m-1 text-xs"
                  />

                  <div className="col-span-1 rounded bg-red-500 m-1 p-1 flex justify-center">
                    <button type="button" onClick={() => handleRemoveNote(index)}>
                      <span className="text-white text-xs font-bold">-</span>
                    </button>
                  </div>
                </div>
              ))}

              <div className="flex justify-start items-center m-1">
                <p className="font-bold text-xs m-1 p-1">Add Note</p>
                <button type="button" onClick={handleAddNote} className="h-5 w-5 text-white m-1 p-1 rounded bg-green-500 flex justify-center items-center">
                  +
                </button>
              </div>
            </div>

            <div id="actions" className="bg-white m-1 px-1 py-3 rounded">
              <EtBtnFormBasic name={"Update"} type={"create"} handleClick={handleSubmit} />
              <EtBtnFormBasic name={"Cancel"} type={"cancel"} handleClick={handleCancel} />
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default EtUpdateAccountContactMap